import JSZip from "jszip";
import { db } from "./db";
import { formatWhen } from "./ui";

export async function buildExportPackage(workingDayId: string): Promise<Blob> {
  const day = await db.workingDays.get(workingDayId);
  if (!day) throw new Error("Working Day not found on this device");

  const [jobs, events, photos, vehicles] = await Promise.all([
    db.jobs.where("workingDayId").equals(workingDayId).toArray(),
    db.events.where("workingDayId").equals(workingDayId).toArray(),
    db.photos.where("workingDayId").equals(workingDayId).toArray(),
    db.vehicles.toArray(),
  ]);

  const vehicle = vehicles.find((v) => v.id === day.vehicleId);
  const dateStamp = day.startedAt.slice(0, 10);

  const lines = [
    "LogProof export package",
    `Working Day: ${day.id}`,
    `Started: ${formatWhen(day.startedAt)}`,
    `Ended: ${day.endedAt ? formatWhen(day.endedAt) : "still open"}`,
    `Status: ${day.status}`,
    `Vehicle: ${vehicle ? `${vehicle.label} (${vehicle.registration})` : day.vehicleId}`,
    "",
    "Jobs",
    ...jobs.map((j) => `- ${j.title}${j.notes ? ` — ${j.notes}` : ""}`),
    "",
    "Events",
    ...events.map(
      (e) =>
        `- ${formatWhen(e.occurredAt)} | ${e.eventType}${e.notes ? ` — ${e.notes}` : ""}`,
    ),
    "",
    "Photographs",
    ...photos.map(
      (p) =>
        `- ${p.filename} | ${p.byteSize} bytes | SHA-256 ${p.sha256} | ${p.verificationStatus}${
          p.verificationStatus === "verified" ? " (Server confirmed)" : ""
        }`,
    ),
    "",
    "Generated by LogProof. Synthetic data. Not an Electronic Work Diary.",
  ];

  const zip = new JSZip();
  zip.file(`logproof-summary-${dateStamp}.txt`, lines.join("\n"));

  for (const photo of photos) {
    if (photo.blob) {
      zip.file(`photos/${photo.filename}`, photo.blob);
    }
  }

  return zip.generateAsync({ type: "blob" });
}

export async function saveOrSharePackage(workingDayId: string): Promise<void> {
  const blob = await buildExportPackage(workingDayId);
  const day = await db.workingDays.get(workingDayId);
  const name = `logproof-${day?.startedAt.slice(0, 10) ?? "export"}.zip`;
  const file = new File([blob], name, { type: "application/zip" });

  const nav = navigator as Navigator & {
    share?: (data: { files?: File[]; title?: string }) => Promise<void>;
    canShare?: (data: { files?: File[] }) => boolean;
  };

  if (nav.share && nav.canShare?.({ files: [file] })) {
    await nav.share({ files: [file], title: "LogProof export" });
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
